import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import Box from 'ui-box';
import { CompanyThumbnail } from '../companies';
import {
  Button,
  Container,
  Heading,
  majorScale,
  minorScale,
  PageContainer,
  PageLoader,
  Tag,
  VirtualTable,
  VirtualTableHeaderItem,
  VirtualTableItem,
} from '../../npaper';
import { SaleThumbnail } from './SaleThumbnail';
import {
  useStores,
  ISale,
  Sale,
  ISaleSnapsnotOut,
  SaleType,
} from '../../stores';
import { useFilterFetch } from '../../stores/utils/useFilterFetch';
import { Spinner } from '../../npaper/Spinner';
import { observer } from 'mobx-react-lite';
import { SaleDetailDialog } from './SaleDetailsDialog';
import {
  FormattedDate,
  FormattedMessage,
  FormattedNumber,
  useIntl,
} from 'react-intl';
import { SaleStatusTag } from './SaleStatusTag';
import { FilterPopover } from '../../npaper/FilterPopover';
import { useHistory, useLocation } from 'react-router-dom';
import { IPaginationFilter } from '../../stores/utils/modelWithCRUD';
import { Logo } from '../Logo';
import { CreditNotesBadge } from './CreditNotesSection';
import { SearchInput } from '../../npaper/SearchInput';
import DateFilter from '../../npaper/DateFilter';
import { ResponsibleFilter } from '../../npaper/ResponsibleFilter';

export const Sales = observer(() => {
  const { saleStore } = useStores();
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();

  const pageType =
    location.pathname.indexOf('offers') > -1
      ? SaleType.OFFER
      : location.pathname.indexOf('orders') > -1
      ? SaleType.ORDER
      : location.pathname.indexOf('invoices') > -1
      ? SaleType.INVOICE
      : undefined;

  const numberFilter =
    pageType === SaleType.OFFER
      ? ['offer_number', 'id']
      : pageType === SaleType.ORDER
      ? ['order_number', 'id']
      : pageType === SaleType.INVOICE
      ? ['invoice_number', 'id']
      : ['invoice_number', 'order_number', 'offer_number', 'id'];

  const defaultOrdering = useMemo(
    () =>
      pageType === SaleType.OFFER
        ? ['created_at']
        : pageType === SaleType.ORDER
        ? ['order_date', 'created_at']
        : pageType === SaleType.INVOICE
        ? ['invoice_created_at']
        : ['created_at'],
    [pageType]
  );

  const getDefaultFilter = useCallback(
    (): IPaginationFilter<ISale>['filter'] => ({
      created_at__gte: undefined,
      created_at__lte: undefined,
      order_date__gte: undefined,
      order_date__lte: undefined,
      invoice_created_at__gte: undefined,
      invoice_created_at__lte: undefined,
      type: pageType,
    }),
    [pageType]
  );

  const { items, isLoading, fetchMore, total, setFilter, filter, reset, sums } =
    useFilterFetch<typeof Sale>(saleStore as any, {
      filter: getDefaultFilter(),
      ordering: defaultOrdering.map((a) => `-${a}`).join(','),
    });

  useEffect(() => {
    reset({
      filter: { ...filter.filter, ...getDefaultFilter() },
      ordering: filter.ordering,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getDefaultFilter]);

  const [isCreatingSale, setIsCreatingSale] = useState(false);

  const createOfferButton = (
    <Button
      iconLeft="add"
      onClick={() => setIsCreatingSale(true)}
      display="inline-flex"
    >
      <FormattedMessage defaultMessage="Create offer" />
    </Button>
  );

  const header: VirtualTableHeaderItem[] = [
    {
      title: intl.formatMessage({ defaultMessage: 'Sale' }),
      width: 120,
      orderByKeys: numberFilter,
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Status' }),
      width: 140,
      orderByKeys: ['status', 'id'],
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Customer' }),
      orderByKeys: ['customer__name', 'id'],
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Title' }),
      orderByKeys: ['title', 'id'],
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Sub total' }),
      align: 'right',
      width: 160,
      orderByKeys: ['sub_total_base', 'id'],
      footer: sums ? (
        <FormattedNumber
          format="currency"
          maximumFractionDigits={0}
          minimumFractionDigits={0}
          value={sums.sub_total_base__sum}
        />
      ) : undefined,
    },
    {
      title: intl.formatMessage({
        defaultMessage: 'CM',
        description: 'Abbreviation for contribution margin',
      }),
      align: 'right',
      width: 160,
      orderByKeys: ['sale_cm'],
      footer:
        sums && sums.sale_cm !== undefined ? (
          <FormattedNumber
            format="currency"
            maximumFractionDigits={0}
            minimumFractionDigits={0}
            value={sums.sale_cm}
          />
        ) : undefined,
    },
    {
      title: intl.formatMessage({
        defaultMessage: 'CMR',
        description: 'Abbreviation for contribution margin ratio',
      }),
      align: 'right',
      width: 110,
      orderByKeys: ['sale_cmr'],
      footer:
        sums && sums.sale_cmr ? (
          <FormattedNumber format="percent" value={sums.sale_cmr} />
        ) : undefined,
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Created' }),
      width: 180,
      orderByKeys:
        pageType === SaleType.OFFER
          ? ['created_at']
          : pageType === SaleType.ORDER
          ? ['order_date']
          : ['invoice_created_at'],
    },
    ...(pageType === SaleType.INVOICE
      ? [
          {
            title: intl.formatMessage({ defaultMessage: 'Invoice date' }),
            width: 180,
            orderByKeys: ['invoice_date'],
          },
        ]
      : []),
    {
      title: intl.formatMessage({ defaultMessage: 'Responsible' }),
      orderByKeys: ['responsible__user__first_name'],
    },
    { title: 'Tags', orderByKeys: ['tags__name', 'id'] },
  ];

  return !items || total === undefined ? (
    <PageContainer>
      <PageLoader />
    </PageContainer>
  ) : (
    <PageContainer
      display="flex"
      flexDirection="column"
      paddingBottom={
        total === 0 && pageType === SaleType.OFFER ? undefined : '0'
      }
      justifyContent={
        total === 0 && pageType === SaleType.OFFER ? 'center' : 'flex-start'
      }
      height="100%"
      maxHeight="100vh"
    >
      <SaleDetailDialog
        isShown={isCreatingSale}
        onClose={(sale) => {
          setIsCreatingSale(false);
          if (sale) {
            history.push(`/sales/${sale.id}`);
          }
        }}
      />
      {total === 0 && pageType === SaleType.OFFER ? (
        <Box textAlign="center" marginX="auto" maxWidth={300}>
          <Logo className="mx-auto" />
          <Heading size={500} marginTop={majorScale(2)}>
            <FormattedMessage defaultMessage="Create an offer" />
          </Heading>
          <Box is="p" marginY={majorScale(1)}>
            <FormattedMessage defaultMessage="Create an offer, add some lines, send it to your customer and close your sale. Fast and simple." />
          </Box>
          {createOfferButton}
        </Box>
      ) : (
        <>
          <Container marginBottom={majorScale(2)} flexShrink={0} flexGrow={0}>
            <Heading size={700} marginBottom={minorScale(3)}>
              {location.pathname.indexOf('offers') > -1
                ? intl.formatMessage({ defaultMessage: 'Offers' })
                : location.pathname.indexOf('orders') > -1
                ? intl.formatMessage({ defaultMessage: 'Orders' })
                : location.pathname.indexOf('invoices') > -1
                ? intl.formatMessage({ defaultMessage: 'Invoices' })
                : intl.formatMessage({ defaultMessage: 'Sales' })}
            </Heading>
            <Box display="flex" justifyContent="space-between">
              <SearchInput
                marginRight={majorScale(1)}
                currentQuery={filter.filter ? filter.filter.search : ''}
                onSearch={(query) => {
                  setFilter({
                    ...filter,
                    filter: { ...filter.filter, search: query },
                  });
                }}
              />
              <FilterPopover<ISaleSnapsnotOut>
                filter={{ ...filter.filter, type: undefined }}
                onChange={(f) =>
                  setFilter({
                    ...filter,
                    filter: { ...f, ...getDefaultFilter() },
                  })
                }
                filterFields={[
                  {
                    field: 'title',
                    label: 'Titel',
                    type: 'string',
                  },
                  {
                    field: 'customer',
                    label: 'Kunde',
                    type: 'company',
                  },
                  {
                    field: 'sub_total_base',
                    label: 'Sub total',
                    type: 'number',
                  },
                  {
                    field: 'tags__name',
                    label: 'Tags',
                    type: 'tag',
                  },
                ]}
              />

              <Box display="flex" marginLeft="auto">
                <DateFilter
                  onSelect={(range) => {
                    let dateFilter: any = {};
                    if (range) {
                      dateFilter = {
                        // From
                        [`${defaultOrdering[0]}__gte`]:
                          range.from.toISOString(),
                        [`${defaultOrdering[0]}__lte`]: range.to
                          ? range.to.toISOString()
                          : undefined,
                      };
                    } else {
                      dateFilter = {
                        // From
                        [`${defaultOrdering[0]}__gte`]: undefined,
                        [`${defaultOrdering[0]}__lte`]: undefined,
                      };
                    }
                    setFilter((f) => ({
                      ...f,
                      filter: { ...f.filter, ...dateFilter },
                    }));
                  }}
                  marginRight={majorScale(1)}
                />
                <ResponsibleFilter
                  onSelect={(members) => {
                    setFilter({
                      filter: {
                        ...filter.filter,
                        responsible__in:
                          members.length > 0
                            ? members.map((m) => m.id)
                            : undefined,
                      },
                      ordering: filter.ordering,
                    });
                  }}
                />
                {createOfferButton}
              </Box>
            </Box>
          </Container>

          <VirtualTable
            isLoading={isLoading}
            items={items}
            total={total}
            ordering={filter.ordering}
            onOrderingChange={(ordering) =>
              setFilter({ filter: filter.filter, ordering })
            }
            noItemsPlaceholder={
              <Box textAlign="center" paddingY={majorScale(6)}>
                <Heading size={500} marginBottom={majorScale(1)}>
                  <FormattedMessage defaultMessage="No matching sales found" />
                </Heading>
                <p>
                  <FormattedMessage
                    defaultMessage="Try changing your filters or <link>see all your sales</link>"
                    values={{
                      link: (chunk: string) => (
                        <span
                          className="link medium"
                          onClick={() =>
                            setFilter({ filter: {}, ordering: filter.ordering })
                          }
                        >
                          {chunk}
                        </span>
                      ),
                    }}
                  />
                </p>
              </Box>
            }
            header={header}
            footer={true}
            onRequestMore={async () => fetchMore()}
            renderRow={({ item }) => (
              <MemoizedRow
                sale={item}
                showInvoiceDate={pageType === SaleType.INVOICE}
              />
            )}
          />
        </>
      )}
    </PageContainer>
  );
});

const MemoizedRow = memo(
  observer(
    ({
      sale,
      showInvoiceDate = false,
    }: {
      sale: ISale;
      showInvoiceDate?: boolean;
    }) => {
      return (
        <>
          <VirtualTableItem fontWeight={600}>
            <SaleThumbnail sale={sale} />
          </VirtualTableItem>
          <VirtualTableItem>
            <SaleStatusTag sale={sale} small />
            {sale.credit_notes.length > 0 && <CreditNotesBadge sale={sale} />}
          </VirtualTableItem>
          <VirtualTableItem>
            {sale.customer ? (
              <CompanyThumbnail company={sale.customer} />
            ) : (
              <Spinner size={16} />
            )}
          </VirtualTableItem>
          <VirtualTableItem>{sale.title}</VirtualTableItem>
          <VirtualTableItem className="font-mono">
            {/* TODO: Default currency... */}
            <Box is="span" marginLeft="auto">
              <FormattedNumber
                format="currency"
                value={sale.sub_total_base}
                currency={sale.sub_total_base_currency}
              />
            </Box>
          </VirtualTableItem>
          <VirtualTableItem className="font-mono">
            <Box is="span" marginLeft="auto">
              <FormattedNumber
                format="currency"
                value={sale.sub_total_base - sale.cost_price_base}
                currency={sale.sub_total_base_currency}
              />
            </Box>
            {/*<ContributionMarginText
              marginLeft="auto"
              contributionMargin={sale.costPricesale.contribution_margin}
            />*/}
          </VirtualTableItem>
          <VirtualTableItem className="font-mono">
            <Box is="span" marginLeft="auto">
              <FormattedNumber
                format="percent"
                value={
                  sale.sub_total_base === 0
                    ? 0
                    : (sale.sub_total_base - sale.cost_price_base) /
                      sale.sub_total_base
                }
                maximumFractionDigits={0}
                minimumFractionDigits={0}
              />
            </Box>
          </VirtualTableItem>
          <VirtualTableItem>
            {sale.type === SaleType.OFFER ? (
              <FormattedDate value={sale.created_at} format="default" />
            ) : sale.type === SaleType.ORDER ? (
              sale.order_date ? (
                <FormattedDate value={sale.order_date} format="default" />
              ) : (
                ''
              )
            ) : sale.invoice_created_at ? (
              <FormattedDate value={sale.invoice_created_at} format="default" />
            ) : (
              ''
            )}
          </VirtualTableItem>
          {showInvoiceDate && (
            <VirtualTableItem>
              {sale.invoice_date && (
                <FormattedDate value={sale.invoice_date} format="default" />
              )}
            </VirtualTableItem>
          )}
          <VirtualTableItem>
            {sale.responsible.length > 0 &&
              (sale.responsible.reduce((p, n) => !!p && !!n, true)
                ? sale.responsible.map((member) => member!.user.name).join(', ')
                : '')}
          </VirtualTableItem>
          <VirtualTableItem>
            {sale.tags.map((t) => (
              <Tag
                key={t}
                small={true}
                marginRight={minorScale(1)}
                intent="info"
              >
                {t}
              </Tag>
            ))}
          </VirtualTableItem>
        </>
      );
    }
  )
);
